import { ApplicationElement, html } from '@effectivastudio/lit-components';
import translations from './translations.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import style from './style.lit.css';
import './../booking_menu/element.js';
import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/time_range/element.js';
import '@effectivastudio/lit-components/components/date_widget/element.js';
import '@effectivastudio/lit-components/components/label/element.js';
import '@effectivastudio/lit-components/components/avatar/element.js';

/**
 * Presents booking slot with date widget ( date from ), avatar, user name, custom option, description, and time range ( from - to )
 *
 * @prop status - Availability status (options: unavailable, reserved, pending, declined, canceled)
 * @prop from - Date from
 * @prop to - Date to
 * @prop dateOnly - Boolean value for all day range ( default "false" )
 * @prop name - User name for display and for avatar initials
 * @prop picture - Url picture for avatar ( optional )
 * @prop option - Custom option ( optional )
 * @prop desc - Additional info ( optional )
 */

export default class BookingSlot extends ApplicationElement {
  /** @internal */
  static translations = translations;
  static styles = [ApplicationElement.styles, style];
  static properties = {
    status: { type: String },
    from: { type: Date },
    to: { type: Date },
    dateOnly: { type: Boolean },
    name: { type: String },
    picture: { type: String },
    option: { type: String },
    desc: { type: String },

    menuOn: { type: Boolean },
    slotId: { type: Number },
    cardId: { type: Number },
    approveOn: { type: Boolean },
    declineOn: { type: Boolean },
    undoOn: { type: Boolean },
    contactOn: { type: Boolean },
    contactUrl: { type: String },
    editOn: { type: Boolean },
    cancelOn: { type: Boolean },
    deleteOn: { type: Boolean },
  };

  /** @internal */
  get classes() {
    return {
      t2: this.status === 'unavailable',
      t3: this.status === 'reserved',
      t4: this.status === 'pending',
      t5: this.status === 'declined' || this.status === 'canceled',
    };
  }

  renderAvatar() {
    return html`
      ${this.status === 'unavailable'
        ? html` <ef-icon name="ban"></ef-icon>`
        : html`
            <div class="avatar">
              <ef-avatar
                picture=${ifDefined(this.picture)}
                name="${this.name}"
                rounded>
              </ef-avatar>
            </div>
          `}
    `;
  }

  get shouldRenderLabel() {
    return (
      this.status === 'pending' ||
      this.status === 'declined' ||
      this.status === 'canceled'
    );
  }

  renderLabel() {
    return html`
      <ef-label
        theme=${this.status === 'declined' || this.status === 'canceled'
          ? 'danger'
          : 'warning'}>
        ${this.t(`status.${this.status}`)}
      </ef-label>
    `;
  }

  renderMenu() {
    return html`
      <bu-booking-menu
        .bookingSlot=${this}
        .slotId=${this.slotId}
        .cardId=${this.cardId}
        .approveOn=${this.approveOn}
        .declineOn=${this.declineOn}
        .undoOn=${this.undoOn}
        .contactOn=${this.contactOn}
        .contactUrl=${this.contactUrl}
        .editOn=${this.editOn}
        .cancelOn=${this.cancelOn}
        .deleteOn=${this.deleteOn}>
      </bu-booking-menu>
    `;
  }

  renderName() {
    return html`
      <div class="name">
        ${this.status === 'unavailable'
          ? this.t('status.unavailable')
          : this.name}
        ${this.shouldRenderLabel ? this.renderLabel() : null}
      </div>
    `;
  }

  renderDetails() {
    return html`
      <div class="details">
        ${this.renderName()}
        ${this.option && html`<div class="option">${this.option}</div>`}
        ${this.desc && html`<div class="description">${this.desc}</div>`}
        <ef-time-range
          from=${this.from}
          to=${this.to}
          dateOnly=${this.dateOnly}>
        </ef-time-range>
      </div>
    `;
  }

  renderContent() {
    return html`
      <div class="content">
        ${this.renderAvatar()} ${this.renderDetails()}
        ${this.menuOn ? this.renderMenu() : null}
      </div>
    `;
  }

  render() {
    return html`
      <article class="${classMap(this.classes)}">
        <ef-date-widget theme="gray" date="${this.from}"></ef-date-widget>
        ${this.renderContent()}
      </article>
    `;
  }
}

customElements.get('bu-booking-slot') ||
  customElements.define('bu-booking-slot', BookingSlot);

BookingSlot.registerTagName('bu-booking-slot');
